import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Button, TextField, Tooltip } from "@mui/material/";
import { useEffect, useState } from "react";

import useMedicalCompleteLock from "../../../../../../hooks/useMedicalCompleteLock";
import useService from "../../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../../global/ApiRequestErrorHandler";
import ResponseModal from "../../../../../global/ResponseModal";
import { MEDICALLY_COMPLETE_LOCK_MESSAGE } from "../../../../../modules/ClientDetail/Case/utils/constants";

import useRevalidateParent from "./../../../../../../hooks/useRevalidateParent";
import ApptBillsAutocomplete from "./ApptBills/ApptBillsAutocomplete";

const initialData = {
  service: "",
  price: "",
  pk: "",
};

export default function CreateApptProcedurev2({
  caseInfo,
  selectedAppt,
  selectedBills,
  setOpenAddBill,
  setSelectedBills,
}) {
  const revalidateParent = useRevalidateParent("rvClientDetail");
  const administeredServiceRepository = useService("repositories/administered-service-repository");
  const { isAppointmentLocked } = useMedicalCompleteLock();
  const billsLocked = isAppointmentLocked(caseInfo);
  const providerPk = selectedAppt.provider.pk;
  const [openSuccess, setOpenSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState({
    title: "",
    description: "",
  });
  const [openError, setOpenError] = useState(false);
  const [errorArray, setErrorArray] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState([initialData]);
  const [servicesOptsByIndex, setServicesOptsByIndex] = useState([[]]);
  const [searchLimitsByIndex, setSearchLimitsByIndex] = useState([25]);
  const [searchTermsByIndex, setSearchTermsByIndex] = useState([""]);

  const isEditionMode = Boolean(selectedBills?.length > 0);
  const allowSave = formData.every(({ price, service }) => !!price && !!service);

  function handleChange(field, value, index) {
    setFormData((prev) =>
      prev.map((current, idx) => (idx === index ? { ...current, [field]: value } : current))
    );
  }

  function handleAddBill() {
    setFormData((prev) => [...prev, initialData]);
    setServicesOptsByIndex((prev) => [...prev, []]);
    setSearchLimitsByIndex((prev) => [...prev, 25]);
    setSearchTermsByIndex((prev) => [...prev, ""]);
  }

  function handleRemoveBill(index) {
    if (formData.length === 1) {
      return;
    }
    setFormData((prev) => prev.filter((_, idx) => idx !== index));
    setServicesOptsByIndex((prev) => prev.filter((_, idx) => idx !== index));
    setSearchLimitsByIndex((prev) => prev.filter((_, idx) => idx !== index));
    setSearchTermsByIndex((prev) => prev.filter((_, idx) => idx !== index));
  }

  function handleErrors(error) {
    const err = error ?? error?.response?.data;
    const errArr = ApiRequestErrorHandler(err);
    setErrorArray(errArr);
    setOpenError(true);
  }

  function handleOpenSuccess() {
    setOpenSuccess(false);
    setOpenAddBill(false);
    if (isEditionMode) {
      setSelectedBills([]);
    }
    revalidateParent();
  }

  async function handleSubmit() {
    if (billsLocked) {
      setErrorArray([MEDICALLY_COMPLETE_LOCK_MESSAGE]);
      setOpenError(true);
      return;
    }

    try {
      setLoading(true);
      const bills = [];

      for (const element of formData) {
        const commonData = {
          administeredTo: caseInfo.client.pk,
          administeredBy: selectedAppt.provider.pk,
          appointment: selectedAppt.pk,
          service: element.service,
          price: element.price,
        };

        bills.push(isEditionMode ? { ...commonData, pk: element.pk } : commonData);
      }

      if (isEditionMode) {
        await administeredServiceRepository.updateBulk(bills);
        setSuccessMessage({
          title: "Administered Service Updated",
          description: "Successfully Edited Administered Service",
        });
      } else {
        await administeredServiceRepository.createBulk(bills);
        setSuccessMessage({
          title: "New Administered Service Added",
          description: "Successfully Added Administered Service",
        });
      }

      setOpenSuccess(true);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isEditionMode || !selectedBills?.length) {
      return;
    }

    async function initializeForm() {
      const initialFormData = [];

      for (let idx = 0; idx < selectedBills.length; idx++) {
        const bill = selectedBills[idx];
        const serviceValueOpt = bill?.service?.pk;
        const serviceLabelOpt = bill?.service
          ? `${bill.service.service_code} - ${bill.service.service_name}`
          : "";

        setServicesOptsByIndex((prev) => {
          const updated = [...prev];
          updated[idx] = serviceValueOpt
            ? [
                {
                  value: serviceValueOpt,
                  label: serviceLabelOpt,
                },
              ]
            : [];
          return updated;
        });

        initialFormData.push({
          service: serviceValueOpt,
          price: bill.price,
          pk: bill.pk,
        });
      }

      setFormData(initialFormData);
    }

    initializeForm();
  }, [isEditionMode, selectedBills]);

  return (
    <div className="flex h-full w-full flex-wrap gap-y-4 p-4">
      <section className="max-h-full w-full">
        <form id="appt-bills-form" className="mb-4 flex w-full flex-wrap gap-4 pt-8">
          {formData.map((form, idx) => (
            <div key={idx} className="grid h-fit w-full grid-cols-[1fr_1fr_auto] gap-4">
              <ApptBillsAutocomplete
                form={form}
                handleChange={handleChange}
                handleErrors={handleErrors}
                idx={idx}
                providerPk={providerPk}
                searchLimitsByIndex={searchLimitsByIndex}
                searchTermsByIndex={searchTermsByIndex}
                servicesOptsByIndex={servicesOptsByIndex}
                setSearchLimitsByIndex={setSearchLimitsByIndex}
                setSearchTermsByIndex={setSearchTermsByIndex}
                setServicesOptsByIndex={setServicesOptsByIndex}
              />

              <TextField
                type="number"
                required
                fullWidth
                label="Administered Service Amount"
                onChange={(e) => handleChange("price", e.target.value, idx)}
                size="small"
                value={form.price}
              />

              {!isEditionMode && (
                <Tooltip title="Remove service" placement="bottom">
                  <span>
                    <Button
                      disabled={formData.length === 1}
                      onClick={() => handleRemoveBill(idx)}
                      className="text-red-500"
                    >
                      <RemoveCircleOutlineIcon />
                    </Button>
                  </span>
                </Tooltip>
              )}
            </div>
          ))}
        </form>

        {!isEditionMode && (
          <div className="mb-4 flex h-fit w-full items-center justify-end">
            <a onClick={handleAddBill} className="cursor-pointer text-xs hover:text-blue-600">
              Add another Administered Service{" "}
              <ControlPointOutlinedIcon className="ml-1 text-sm text-green-500" />
            </a>
          </div>
        )}
      </section>

      {billsLocked && <p className="my-4 text-red-600">{MEDICALLY_COMPLETE_LOCK_MESSAGE}</p>}

      <div className="flex w-full justify-center self-end">
        <Button
          disabled={loading || !allowSave || billsLocked}
          onClick={handleSubmit}
          variant="contained"
          className="bg-blue-800 normal-case text-white hover:bg-blue-700"
        >
          {isEditionMode ? "Update " : "Create "} Administered Services
        </Button>
      </div>

      <ResponseModal
        title={openSuccess ? successMessage.title : "Error Adding Administered Service"}
        description={openSuccess ? successMessage.description : ""}
        errorMessage={openError ? errorArray : ""}
        isError={openError}
        openBool={openSuccess || openError}
        setOpenBool={openSuccess ? setOpenSuccess : setOpenError}
        handleCloseFunc={openSuccess ? handleOpenSuccess : undefined}
      />
    </div>
  );
}
